<template>
  <transition name="slide" appear>
    <div class="point-panel" ref="panel" v-if="point.id" id="point-panel">
      <div class="point-panel-container">
        <div v-if="editModeEnabled">
          <component
            v-if="canUpdate && componentId"
            :is="`${pointType}-editor`"
            :id="componentId"
            :point="point"
            @save="afterChildSave"
            @destroy="destroy"
            @cancel="cancelEdit"
            @close="close"
          >
            <template #floor>
              <b-form-group label="Floor">
                <map-floor-select @change="changeFloor" :active-floor="point.floor_id" />
              </b-form-group>
            </template>
          </component>
        </div>
        <div v-else>
          <component
            v-if="canRead && componentId"
            :is="`${pointType}-viewer`"
            :id="componentId"
            :point="point"
            @edit="setEdit(true)"
            @close="close"
          />
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import DefaultViewer from '@/components/map/MapDefaultViewer.vue'
import PrinterEditor from '@/components/printer/PrinterEditor.vue'
import PrinterViewer from '@/components/printer/PrinterViewer.vue'
import EmployeeEditor from '@/components/employee/EmployeeEditor.vue'
import EmployeeViewer from '@/components/employee/EmployeeViewer.vue'
import RoomEditor from '@/components/room/RoomEditor.vue'
import RoomViewer from '@/components/room/RoomViewer.vue'
import PointViewer from '@/components/point/PointViewer.vue'
import PointEditor from '@/components/point/PointEditor.vue'
import DeskEditor from '@/components/desk/DeskEditor.vue'
import DeskViewer from '@/components/desk/DeskViewer.vue'
import PcEditor from '@/components/pc/PcEditor.vue'
import PcViewer from '@/components/pc/PcViewer.vue'

import { isLocalId } from '@/utils/Id'
import { mapState, mapActions, mapMutations, mapGetters } from 'vuex'
import * as PN from '@/utils/PermissionNames'
import MapFloorSelect from '@/components/map/MapFloorSelect.vue'
import { POINT_DESTROY, POINT_ICONS, POINT_TYPE_MAP, POINT_UPDATE } from '@/store/modules/point'
import { MAP_UPDATE } from '@/store/modules/map'
import AreaEditor from '@/components/area/AreaEditor.vue'
import AreaViewer from '@/components/area/AreaViewer.vue'

export default {
  name: 'MapPointPanel',
  components: {
    AreaViewer,
    AreaEditor,
    MapFloorSelect,
    RoomEditor,
    RoomViewer,
    EmployeeEditor,
    EmployeeViewer,
    PrinterViewer,
    PrinterEditor,
    DefaultViewer,
    PointViewer,
    PointEditor,
    DeskEditor,
    DeskViewer,
    PcEditor,
    PcViewer
  },
  data: () => ({
    PN
  }),
  watch: {
    activePointId: {
      immediate: true,
      handler() {
        if (!this.canUpdate && this.canRead) {
          this.setEdit(false)
        }
      }
    }
  },
  computed: {
    ...mapState('auth', ['user']),
    ...mapState('map', ['map', 'activePointId', 'editMode']),
    ...mapGetters('point', ['getPoint']),
    ...mapGetters('rbac', ['can']),
    ...mapGetters('employee', ['getEmployee']),
    permissionModel() {
      return (this.pointType === 'area' ? 'point' : this.pointType).toLocaleUpperCase()
    },
    canRead() {
      return this.can(`${this.permissionModel}_READ`)
    },
    canUpdate() {
      return this.pointType === 'desk' ? this.can('DESK_EVENT_UPDATE') : this.can(`${this.permissionModel}_UPDATE`)
    },
    componentId() {
      if (['point', 'area'].includes(this.pointType)) {
        return this.activePointId
      }

      return this.point[this.pointType]
    },
    editModeEnabled() {
      return this.editMode === true
    },
    isNew() {
      return isLocalId(this.activePointId)
    },
    point() {
      return this.getPoint(this.activePointId) || {}
    },
    pointType() {
      return POINT_TYPE_MAP[this.point.icon_id] ?? 'point'
    }
  },
  methods: {
    ...mapActions('point', ['pointDestroy', 'pointLoad']),
    ...mapMutations('map', {
      mapUpdate_: MAP_UPDATE
    }),
    ...mapMutations('point', {
      pointUpdate_: POINT_UPDATE,
      pointDestroy_: POINT_DESTROY
    }),
    close() {
      this.mapUpdate_({ activePointId: null })
    },
    async destroy() {
      if (this.point.icon_id === POINT_ICONS.EMPLOYEE && this.user?.booking_enabled) {
        return
      }
      await this.pointDestroy(this.point)
      this.close()
    },
    async cancelEdit() {
      if (this.isNew) {
        this.pointDestroy_(this.point)
        this.close()
      } else {
        this.pointLoad({ id: this.point.id })
        this.setEdit(false)
      }
    },
    changeFloor(floorId) {
      this.pointUpdate_({ id: this.activePointId, floor_id: floorId })
    },
    afterChildSave({ pointId }) {
      const floorId = this.point.floor_id
      if (this.isNew) {
        this.pointDestroy_(this.point)
        this.mapUpdate_({ activePointId: pointId })
      }
      this.mapUpdate_({ floorId })
      this.setEdit(false)
    },
    setEdit(editMode) {
      this.mapUpdate_({ editMode })
    }
  }
}
</script>

<style scoped lang="scss">
.point-panel {
  background: #fff;
  position: fixed;
  overflow-y: scroll;
  height: 100%;
  right: 0;
  top: 66px;
  padding: 0 0 66px 0;
  box-shadow: 0 0 20px 0 rgba(0, 0, 0, 0.2);
  z-index: 1074;
  width: 100%;

  &.modal-enabled {
    overflow-y: clip;
  }

  &-container {
    position: relative;
    min-height: 100%;
  }

  @media print {
    display: none;
  }

  @media (min-width: 600px) {
    flex: 0 0 100%;
    max-width: 100%;
    .desk-destroy {
      right: 80px;
    }
  }

  @media (min-width: 800px) {
    flex: 0 0 60%;
    max-width: 60%;
    .desk-destroy {
      right: calc(60% - 80px);
    }
  }

  @media (min-width: 1000px) {
    flex: 0 0 33%;
    max-width: 33%;
    .desk-destroy {
      right: calc(33% - 80px);
    }
  }

  @media (min-width: 1200px) {
    flex: 0 0 28%;
    max-width: 28%;
    .desk-destroy {
      right: calc(28% - 80px);
    }
  }

  @media (min-width: 1600px) {
    flex: 0 0 25%;
    max-width: 25%;
    .desk-destroy {
      right: calc(25% - 80px);
    }
  }

  @media (min-width: 1900px) {
    flex: 0 0 20%;
    max-width: 20%;
    .desk-destroy {
      right: calc(20% - 80px);
    }
  }

  @media (min-width: 2200px) {
    flex: 0 0 15%;
    max-width: 15%;
    .desk-destroy {
      right: calc(15% - 80px);
    }
  }
}

.type-select {
  text-align: center;
}

.slide-enter,
.slide-leave-active {
  transform: translate(100%, 0);
}

.slide-enter-active,
.slide-leave-active {
  transition: transform 0.5s;
}
</style>
